import { toast } from "@factory/design-system";
import { nanoid } from "nanoid";

import * as accountingApi from "@/apiv2/accounting";
import * as billingApi from "@/apiv2/billing";
import * as teamsApi from "@/apiv2/teams";

import * as alertActions from "../../alerts/alerts.actions";
import { handleError } from "../../utils/error";

import { buildInviteResultMessage } from "./invite-result-message";
import InviteResultToast from "./InviteResultToast";
import {
  RESET,
  SET_ACTIVE_TEAM_MEMBER,
  SET_EMPLOYEE_LIST,
  SET_INVITE_LINK,
  SET_IS_FETCHING,
  SET_IS_FETCHING_INVITE_LINK,
  SET_IS_SAVING,
  SET_IS_SENDING_INVITE,
  SET_LISTS,
} from "./teams.actionTypes";
import * as selectors from "./teams.selectors";

import styles from "./teams.module.scss";

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setIsSaving = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_SAVING,
    payload: data,
  });
};

export const setIsFetching = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING,
    payload: data,
  });
};

export const setLists = (data) => (dispatch) => {
  dispatch({
    type: SET_LISTS,
    payload: data,
  });
};

export const setActiveTeamMember = (data) => (dispatch) => {
  dispatch({
    type: SET_ACTIVE_TEAM_MEMBER,
    payload: data,
  });
};

export const setInviteLink = (data) => (dispatch) => {
  dispatch({
    type: SET_INVITE_LINK,
    payload: data,
  });
};

export const setIsFetchingInviteLink = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_INVITE_LINK,
    payload: data,
  });
};

export const setIsSendingInvite = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_SENDING_INVITE,
    payload: data,
  });
};

export const setEmployeeList = (data) => (dispatch) => {
  dispatch({
    type: SET_EMPLOYEE_LIST,
    payload: data,
  });
};

export const fetchTeams = (params) => async (dispatch) => {
  try {
    dispatch(setIsFetching(true));

    const { data } = await teamsApi.getTeams(params)();
    dispatch(setLists(data));
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsFetching(false));
  }
};

export const createTeamMember = (data) => async (dispatch) => {
  try {
    dispatch(setIsSaving(true));

    await teamsApi.postTeams({
      ...data,
    })();
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsSaving(false));
  }
};

export const updateTeamMember = (data) => async (dispatch, getState) => {
  try {
    const state = getState();
    const teams = selectors.getLists(state);
    dispatch(setIsSaving(true));

    await teamsApi.putTeam(data)();

    dispatch(
      setLists(
        teams.map((t) =>
          t.id === data.id
            ? {
                ...t,
                email: data.email,
                firstName: data.first_name,
                lastName: data.last_name,
                role: data.role,
                isPlannerAssigneeOnly: data.is_planner_assignee_only,
                isActive: data.is_active,
                ...(data.color !== undefined && { color: data.color }),
              }
            : t
        )
      )
    );
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsSaving(false));
  }
};

export const updateTeamMemberColor = (row, color) => async (dispatch, getState) => {
  try {
    // Dedicated colour endpoint: only touches profile.color. The full-user PUT would
    // re-inject is_active=True and silently re-activate a disabled member.
    await teamsApi.patchTeamColor(row.id, color)();

    const teams = selectors.getLists(getState());
    dispatch(setLists(teams.map((t) => (t.id === row.id ? { ...t, color } : t))));
  } catch (error) {
    handleError(error, dispatch);
  }
};

export const fetchInviteLink = () => async (dispatch) => {
  try {
    dispatch(setIsFetchingInviteLink(true));
    const { data } = await teamsApi.postLinkInvite()();
    dispatch(setInviteLink(`${process.env.BASE_URL}/invite/${data.inviteCode}`));
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsFetchingInviteLink(false));
  }
};

// One request, two intents. Inviting reports addresses that already have an
// invite instead of quietly mailing them again; resending must always mail.
// The skip_existing flag is what separates them, so the two entry points below
// are not interchangeable.
const TOAST_OPTIONS = {
  position: "top-center",
  // Short timer, with a close button for anyone who wants it gone sooner. The
  // toast carrying the Resend link shares it, so that link is only live for
  // three seconds.
  autoClose: 3000,
  closeButtonVisible: true,
  // 12px and centred, as drawn. The shared toast's is 16px and top-aligned.
  closeButtonClassName: styles.toastClose,
};

const runInvite =
  ({ payload, skipExisting }) =>
  async (dispatch) => {
    const { emails = [], invalidEmails = [] } = payload;

    // Every address failed validation, so there is nothing to ask the backend
    // about. Report it without a round trip.
    if (emails.length === 0 && invalidEmails.length > 0) {
      const { message } = buildInviteResultMessage({ invalid: invalidEmails });
      toast.error(message, {
        ...TOAST_OPTIONS,
        className: styles.invitedToast,
        "data-testid": "invite-failed-toast",
      });
      return { everythingSent: false };
    }

    try {
      dispatch(setIsSendingInvite(true));

      // invalidEmails is ours, not the endpoint's - build the body explicitly
      // rather than spreading the payload, so it does not leak into the request.
      const { invalidEmails: _ignored, ...body } = payload;
      const { data } = await teamsApi.postEmailInvite(
        skipExisting ? { ...body, skip_existing: true } : body
      )();

      if (emails.length === 0) {
        return { everythingSent: false };
      }

      // camelCase, not the backend's snake_case: the apiv2 layer runs every
      // response through camelcaseKeys (see apiv2/common.js).
      const { message, resendEmails, tone } = buildInviteResultMessage({
        sent: data?.sent,
        alreadyInvited: data?.alreadyInvited,
        alreadyMember: data?.alreadyMember,
        invalid: invalidEmails,
      });

      const onResend = (emails) =>
        dispatch(resendInviteToEmails({ ...payload, emails }));

      const showToast = tone === "success" ? toast.success : toast;

      showToast(
        // A node, not a string: the message interpolates addresses the user
        // typed and the toast can carry a Resend control.
        <InviteResultToast message={message} resendEmails={resendEmails} onResend={onResend} />,
        // Centred rather than the container's top-right default: this confirms
        // an action the user just took in a modal that has since closed, so it
        // belongs where they were looking. The class clears the global header,
        // which the design system's top-center container does not.
        {
          ...TOAST_OPTIONS,
          className: styles.invitedToast,
          "data-testid": "invite-sent-toast",
        }
      );

      // True only when nothing at all was left over. The modal closes on this,
      // so deriving it from "something was sent" would close on a partial send
      // and lose the addresses the user still has to fix.
      return {
        everythingSent:
          invalidEmails.length === 0 &&
          (data?.alreadyInvited?.length ?? 0) === 0 &&
          (data?.alreadyMember?.length ?? 0) === 0,
      };
    } catch (error) {
      // Fixed copy rather than the backend's message. A failure here is either
      // a bad address or an internal error, and neither is something the raw
      // message helps with. Session and trial handling still happen in the
      // axios interceptor, so replacing handleError here loses nothing.
      toast.error("Couldn't send the invite. Check the email address and try again.", {
        ...TOAST_OPTIONS,
        className: styles.invitedToast,
        "data-testid": "invite-failed-toast",
      });
      // Rethrow so the caller does not close the modal on a failed send.
      throw error;
    } finally {
      dispatch(setIsSendingInvite(false));
    }
  };

// Inviting: report already-invited addresses rather than mailing them again.
export const sendInviteToEmails = (payload) => runInvite({ payload, skipExisting: true });

// Resending: always mail, whether or not an invite already exists.
export const resendInviteToEmails = (payload) => runInvite({ payload, skipExisting: false });

export const removeInvitedTeam = (payload) => async (dispatch) => {
  try {
    await teamsApi.deleteEmailInvite(payload)();

    dispatch(
      alertActions.createAlertAction(nanoid(), "Invite link has been cancelled.", true, "error")
    );
  } catch (error) {
    handleError(error, dispatch);
  }
};

export const updateBillingSubscriptions = () => async (dispatch) => {
  try {
    await billingApi.putBillingScriptions()();
  } catch (error) {
    handleError(error, dispatch);
  }
};

export const fetchMyobEmployees = (params) => async (dispatch) => {
  try {
    const { data } = await accountingApi.getMyobEmployees(params)();
    return data;
  } catch (error) {
    handleError(error, dispatch);
  }
};

export const fetchQboEmployees = (params) => async (dispatch) => {
  try {
    const { data } = await accountingApi.getQboEmployees(params)();
    return data;
  } catch (error) {
    handleError(error, dispatch);
  }
};

export const fetchXeroEmployees = (params) => async (dispatch) => {
  try {
    const { data } = await accountingApi.getXeroEmployees(params)();
    return data;
  } catch (error) {
    handleError(error, dispatch);
  }
};
